import React, { useEffect, useMemo, useState } from 'react';
import { fechaDMA } from '../utils/fechas';
import { obtenerActividadUsuariosApi } from '../services/api';

const filtroClase = 'mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus-visible:border-emerald-600 focus-visible:ring-2 focus-visible:ring-emerald-600/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';
const botonClase = 'rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-emerald-300 hover:text-[#006d44] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';

function formatearFechaHora(valor) {
  const raw = String(valor || '').trim();
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
}

function formatearDetalleActividad(valor) {
  const raw = String(valor || '').trim();
  if (!raw) return '-';
  try {
    const parsed = JSON.parse(raw);
    const partes = [];
    if (parsed?.fechaInicio || parsed?.fechaFin) {
      partes.push(`Periodo: ${fechaDMA(parsed.fechaInicio) || '-'} a ${fechaDMA(parsed.fechaFin) || '-'}`);
    }
    if (parsed?.fecha) {
      partes.push(`Fecha: ${fechaDMA(parsed.fecha)}`);
    }
    if (Array.isArray(parsed?.tratamientos)) {
      partes.push(`Tratamientos: ${parsed.tratamientos.join(', ')}`);
    }
    if (parsed?.tratamiento) {
      partes.push(`Tratamiento: ${parsed.tratamiento}`);
    }
    if (parsed?.dni) {
      partes.push(`DNI: ${parsed.dni}`);
    }
    if (typeof parsed?.isAdmin === 'boolean') {
      partes.push(`Admin: ${parsed.isAdmin ? 'Si' : 'No'}`);
    }
    if (typeof parsed?.aplicarTratamientos === 'boolean') {
      partes.push(`Aplica tratamientos: ${parsed.aplicarTratamientos ? 'Si' : 'No'}`);
    }
    if (Array.isArray(parsed?.campos) && parsed.campos.length) {
      partes.push(`Campos: ${parsed.campos.join(', ')}`);
    }
    if (parsed?.anterior || parsed?.actual) {
      partes.push(`Cambio: ${parsed.anterior || '-'} -> ${parsed.actual || '-'}`);
    }
    if (parsed?.autorizadoDesde || parsed?.autorizadoHasta) {
      partes.push(`Autorizacion: ${fechaDMA(parsed.autorizadoDesde) || '-'} a ${fechaDMA(parsed.autorizadoHasta) || '-'}`);
    }
    if (parsed?.fechaBaja) {
      partes.push(`Fecha de baja: ${fechaDMA(parsed.fechaBaja)}`);
    }
    if (parsed?.mes) {
      partes.push(`Mes: ${parsed.mes}`);
    }
    if (parsed?.dia || parsed?.hora) {
      partes.push(`Turno: ${parsed.dia || '-'} ${parsed.hora || '-'}`);
    }
    if (parsed?.estado) {
      partes.push(`Estado: ${parsed.estado}`);
    }
    return partes.length ? partes.join(' | ') : raw;
  } catch (err) {
    return raw;
  }
}

function formatearAccion(valor) {
  if (valor === 'create') return 'Creo';
  if (valor === 'delete') return 'Elimino';
  if (valor === 'update') return 'Actualizo';
  if (valor === 'toggle') return 'Cambio';
  return String(valor || '-');
}

function formatearTipo(valor) {
  const mapa = {
    attendance: 'Asistencia',
    patient: 'Paciente',
    patient_discharge: 'Baja de paciente',
    patient_request: 'Solicitud',
    patient_status: 'Estado del paciente',
    patient_treatment: 'Tratamiento del paciente',
    turn: 'Turno',
    turn_exception: 'Excepcion de turno',
    user: 'Usuario',
  };
  return mapa[valor] || String(valor || '-');
}

function normalizarBusqueda(valor) {
  return String(valor || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function obtenerElementoActividad(item) {
  const patientName = String(item.patientName || '').trim();
  if (patientName) return patientName;
  const entityType = String(item.entityType || '').trim();
  const entityLabel = String(item.entityLabel || '').trim();
  const entityId = String(item.entityId || '').trim();
  if (entityType === 'patient' && entityLabel && !/^p-\d+/i.test(entityLabel)) {
    return entityLabel;
  }
  if (
    ['attendance', 'patient_discharge', 'patient_request', 'patient_status', 'patient_treatment', 'turn', 'turn_exception'].includes(
      entityType
    )
  ) {
    return entityLabel && !/^p-\d+/i.test(entityLabel) ? entityLabel : '-';
  }
  return entityLabel || entityId || '-';
}

export default function UserHistory() {
  const [actividad, setActividad] = useState([]);
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroPaciente, setFiltroPaciente] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  async function cargar() {
    setCargando(true);
    setError('');
    try {
      const actividadData = await obtenerActividadUsuariosApi();
      setActividad(actividadData);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el historial.');
      setActividad([]);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  const actividadFiltrada = useMemo(
    () =>
      actividad.filter((item) => {
        const usuario = String(item.actorUsername || '').trim();
        const pacienteClave = String(item.patientId || item.patientName || '').trim();
        const pacienteTexto = normalizarBusqueda(
          `${item.patientName || ''} ${item.patientId || ''} ${obtenerElementoActividad(item)}`
        );
        if (filtroUsuario && usuario.toLowerCase() !== filtroUsuario.toLowerCase()) return false;
        if (filtroPaciente) {
          const pacienteFiltro = normalizarBusqueda(filtroPaciente);
          if (pacienteClave !== filtroPaciente && !pacienteTexto.includes(pacienteFiltro)) return false;
        }
        if (filtroAccion && String(item.actionType || '') !== filtroAccion) return false;
        if (filtroTipo && String(item.entityType || '') !== filtroTipo) return false;
        return true;
      }),
    [actividad, filtroAccion, filtroPaciente, filtroTipo, filtroUsuario]
  );

  const usuariosConActividad = useMemo(
    () =>
      Array.from(
        new Set(actividad.map((item) => String(item.actorUsername || '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, 'es')),
    [actividad]
  );

  const pacientesConActividad = useMemo(() => {
    const map = new Map();
    actividad.forEach((item) => {
      const nombre = String(item.patientName || '').trim();
      const id = String(item.patientId || '').trim();
      const label = String(item.entityLabel || '').trim();
      const tipo = String(item.entityType || '').trim();
      if (!nombre && !id && !(tipo === 'patient' && label && !/^p-\d+/i.test(label))) return;
      map.set(id || nombre || label, { id, nombre: nombre || label || id });
    });
    return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [actividad]);

  const accionesConActividad = useMemo(
    () =>
      Array.from(new Set(actividad.map((item) => String(item.actionType || '').trim()).filter(Boolean))).sort(),
    [actividad]
  );

  const tiposConActividad = useMemo(
    () =>
      Array.from(new Set(actividad.map((item) => String(item.entityType || '').trim()).filter(Boolean))).sort(
        (a, b) => formatearTipo(a).localeCompare(formatearTipo(b), 'es')
      ),
    [actividad]
  );

  const limpiarFiltros = () => {
    setFiltroUsuario('');
    setFiltroPaciente('');
    setFiltroAccion('');
    setFiltroTipo('');
  };

  return (
    <section className="mx-auto max-w-[1440px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="border-b border-slate-200 pb-5 dark:border-slate-700">
        <h2 className="mb-1 text-2xl font-extrabold text-slate-800 dark:text-slate-100">Historial por usuario</h2>
        <p className="text-sm text-slate-600 dark:text-slate-300">Consultá los cambios realizados en el sistema y filtrá por usuario, paciente o tipo de movimiento.</p>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800 sm:p-6">
        <h3 className="mb-4 text-lg font-bold text-slate-800 dark:text-slate-100">Filtros</h3>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Usuario
          <select className={filtroClase} value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)}>
            <option value="">Todos</option>
            {usuariosConActividad.map((username) => (
              <option key={username} value={username}>
                {username}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Paciente
          <select
            value={filtroPaciente}
            onChange={(e) => setFiltroPaciente(e.target.value)}
            className={filtroClase}
          >
            <option value="">Todos</option>
            {pacientesConActividad.map((paciente) => (
              <option key={paciente.id || paciente.nombre} value={paciente.id || paciente.nombre}>
                {paciente.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Acción
          <select
            value={filtroAccion}
            onChange={(e) => setFiltroAccion(e.target.value)}
            className={filtroClase}
          >
            <option value="">Todas</option>
            {accionesConActividad.map((accion) => (
              <option key={accion} value={accion}>
                {formatearAccion(accion)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
          Tipo
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className={filtroClase}
          >
            <option value="">Todos</option>
            {tiposConActividad.map((tipo) => (
              <option key={tipo} value={tipo}>
                {formatearTipo(tipo)}
              </option>
            ))}
          </select>
        </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className="rounded-xl bg-[#006d44] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#005c39] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600" onClick={cargar}>Actualizar historial</button>
          <button type="button" className={botonClase} onClick={limpiarFiltros}>Limpiar filtros</button>
        </div>
      </div>
      {error && <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">{error}</div>}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-5 py-4 dark:border-slate-700 sm:px-6">
          <h3 className="mb-0 text-lg font-bold text-slate-800 dark:text-slate-100">Movimientos</h3>
          <span role="status" className="text-sm text-slate-500 dark:text-slate-300">Mostrando {actividadFiltrada.length} de {actividad.length}</span>
        </div>
        {cargando ? <p className="px-6 py-8 text-sm text-slate-500">Cargando historial...</p> : !actividadFiltrada.length ? (
          <p className="px-6 py-8 text-sm text-slate-500 dark:text-slate-300">{actividad.length ? 'No hay movimientos con estos filtros. Probá limpiarlos.' : 'No hay actividad registrada todavía.'}</p>
        ) : <div className="overflow-x-auto"><table className="w-full min-w-[960px]">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"><tr>
            <th scope="col" className="px-4 py-3">Fecha</th><th scope="col" className="px-4 py-3">Usuario</th><th scope="col" className="px-4 py-3">Acción</th><th scope="col" className="px-4 py-3">Tipo</th><th scope="col" className="px-4 py-3">Elemento</th><th scope="col" className="px-4 py-3">Detalle</th>
          </tr></thead>
          <tbody>{actividadFiltrada.map((item) => <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
            <td className="whitespace-nowrap border-b border-slate-100 px-4 py-3.5 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-300">{formatearFechaHora(item.createdAt)}</td>
            <td className="border-b border-slate-100 px-4 py-3.5 text-sm font-semibold text-slate-800 dark:border-slate-700 dark:text-slate-100">{item.actorUsername || '-'}</td>
            <td className="border-b border-slate-100 px-4 py-3.5 text-sm dark:border-slate-700"><span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{formatearAccion(item.actionType)}</span></td>
            <td className="border-b border-slate-100 px-4 py-3.5 text-sm dark:border-slate-700">{formatearTipo(item.entityType)}</td>
            <td className="border-b border-slate-100 px-4 py-3.5 text-sm dark:border-slate-700">{obtenerElementoActividad(item)}</td>
            <td className="max-w-sm break-words border-b border-slate-100 px-4 py-3.5 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">{formatearDetalleActividad(item.details)}</td>
          </tr>)}</tbody>
        </table></div>}
      </div>
    </section>
  );
}
